import React, { useMemo } from 'react';
import { Badge } from './ui/badge';
import {
  TrendingUp,
  TrendingDown,
  Minus
} from 'lucide-react';

// Simple chart visualization using CSS and DOM (no external chart library)
const PriceTrendChart = ({
  data = [],
  timeRange = '12m',
  showTrendLine = true,
  showAverage = true,
  height = 200,
  interactive = true
}) => {
  const processedData = useMemo(() => {
    if (!data || data.length === 0) return { points: [], stats: {} };

    // Sort data by date
    const sortedData = [...data].sort((a, b) => new Date(a.date) - new Date(b.date));

    // Calculate statistics
    const prices = sortedData.map(d => d.price);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const avgPrice = prices.reduce((sum, price) => sum + price, 0) / prices.length;

    // Calculate trend
    const firstPrice = prices[0];
    const lastPrice = prices[prices.length - 1];
    const priceChange = lastPrice - firstPrice;
    const priceChangePercent = ((priceChange / firstPrice) * 100).toFixed(1);

    // Create chart points
    const points = sortedData.map((item, index) => {
      const x = (index / (sortedData.length - 1)) * 100;
      const y = ((maxPrice - item.price) / (maxPrice - minPrice)) * 100;

      return {
        ...item,
        x,
        y: isNaN(y) ? 50 : y,
        isLatest: index === sortedData.length - 1
      };
    });

    return {
      points,
      stats: {
        minPrice,
        maxPrice,
        avgPrice,
        priceChange,
        priceChangePercent,
        trend: priceChange > 0 ? 'up' : priceChange < 0 ? 'down' : 'flat',
        dataCount: sortedData.length
      }
    };
  }, [data]);

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="h-4 w-4" />;
      case 'down':
        return <TrendingDown className="h-4 w-4" />;
      default:
        return <Minus className="h-4 w-4" />;
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'up':
        return 'text-success';
      case 'down':
        return 'text-destructive';
      default:
        return 'text-muted-foreground';
    }
  };

  if (!data || data.length === 0) {
    return (
      <div
        className="flex items-center justify-center border border-gray-200 rounded-lg bg-muted"
        style={{ height }}
      >
        <span className="text-sm text-muted-foreground">
          暂无价格历史数据
        </span>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Chart Statistics Header */}
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className={getTrendColor(processedData.stats.trend)}>
            {getTrendIcon(processedData.stats.trend)}
          </span>
          <span className="text-sm font-bold">价格趋势</span>
          <Badge
            variant={processedData.stats.trend === 'up' ? 'default' :
                    processedData.stats.trend === 'down' ? 'destructive' : 'secondary'}
            className={processedData.stats.trend === 'up' ? 'bg-success/100' : ''}
          >
            {processedData.stats.priceChangePercent}%
          </Badge>
        </div>
        <span className="text-xs text-muted-foreground">
          {processedData.stats.dataCount} 个数据点
        </span>
      </div>

      {/* Simple Chart Container */}
      <div
        className="relative border border-gray-200 rounded-lg bg-white overflow-hidden"
        style={{ height }}
      >
        {/* Chart SVG */}
        <svg
          width="100%"
          height="100%"
          style={{ position: 'absolute', top: 0, left: 0 }}
        >
          {/* Grid lines */}
          <defs>
            <pattern
              id="grid"
              width="20%"
              height="25%"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 20 0 L 0 0 0 25"
                fill="none"
                stroke="#e5e7eb"
                strokeWidth="0.5"
                opacity="0.3"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />

          {/* Average line */}
          {showAverage && (
            <line
              x1="0%"
              y1={`${((processedData.stats.maxPrice - processedData.stats.avgPrice) / (processedData.stats.maxPrice - processedData.stats.minPrice)) * 100}%`}
              x2="100%"
              y2={`${((processedData.stats.maxPrice - processedData.stats.avgPrice) / (processedData.stats.maxPrice - processedData.stats.minPrice)) * 100}%`}
              stroke="#f59e0b"
              strokeWidth="1"
              strokeDasharray="5,5"
              opacity="0.7"
            />
          )}

          {/* Price line */}
          {processedData.points.length > 1 && (
            <polyline
              fill="none"
              stroke="#3b82f6"
              strokeWidth="2"
              points={processedData.points.map(p => `${p.x},${p.y}`).join(' ')}
            />
          )}

          {/* Data points */}
          {processedData.points.map((point, index) => (
            <g key={index}>
              <circle
                cx={`${point.x}%`}
                cy={`${point.y}%`}
                r={point.isLatest ? "4" : "3"}
                fill={point.isLatest ? "#8b5cf6" : "#3b82f6"}
                stroke="white"
                strokeWidth="2"
                style={{ cursor: interactive ? 'pointer' : 'default' }}
                opacity={point.isLatest ? 1 : 0.8}
              >
                {interactive && (
                  <title>
                    {`日期: ${new Date(point.date).toLocaleDateString('zh-CN')}\n价格: ¥${point.price.toFixed(2)}\n客户: ${point.client_name || '未知'}\n类型: ${point.source_type === 'quotation' ? '报价单' : '销售订单'}`}
                  </title>
                )}
              </circle>
            </g>
          ))}
        </svg>

        {/* Price labels */}
        <div
          className="absolute flex flex-col justify-between py-2"
          style={{ left: -60, top: 0, height: '100%' }}
        >
          <span className="text-xs text-muted-foreground">
            ¥{processedData.stats.maxPrice.toFixed(2)}
          </span>
          {showAverage && (
            <span
              className="absolute text-xs text-warning"
              style={{
                top: `${((processedData.stats.maxPrice - processedData.stats.avgPrice) / (processedData.stats.maxPrice - processedData.stats.minPrice)) * 100}%`,
                transform: 'translateY(-50%)'
              }}
            >
              平均
            </span>
          )}
          <span className="text-xs text-muted-foreground">
            ¥{processedData.stats.minPrice.toFixed(2)}
          </span>
        </div>
      </div>

      {/* Chart Statistics Footer */}
      <div className="flex justify-between items-center mt-2 flex-wrap gap-2">
        <span className="text-xs text-muted-foreground">
          最高: ¥{processedData.stats.maxPrice.toFixed(2)} | 最低: ¥{processedData.stats.minPrice.toFixed(2)}
        </span>
        {showAverage && (
          <span className="text-xs text-warning">
            平均: ¥{processedData.stats.avgPrice.toFixed(2)}
          </span>
        )}
      </div>
    </div>
  );
};

export default PriceTrendChart;
